import { CLIENT_EVENT, EventLogger } from "@app/config/loggers/eventLogger";
import type { Document } from "@base/model/types/document";
import { AppLink } from "@base/ui/AppLink";
import { DateTile } from "@base/ui/DateTile";
import { Chip } from "@buildblockinc/buildblock-design-system";
import { ErrorBoundary } from "@sentry/react";
import dayjs from "dayjs";
import { Lock } from "lucide-react";
import { Suspense } from "react";
import { useRecentDailyLogsQuery } from "../model/useRecentDailyLogsQuery";

const SKELETON_COUNT = 3;

/** 일지 제목은 "[YYYY-MM-DD] …" 형식이다. 작업 날짜가 제목에 없으면 수정일로 대신한다 */
const TITLE_DATE_PATTERN = /^\[(\d{4}-\d{2}-\d{2})\]/;

function getWorkDate(document: Document): string {
  return TITLE_DATE_PATTERN.exec(document.title)?.[1] ?? document.modified_at;
}

function RecentDailyLogsCardSkeleton() {
  return (
    <ol className="mt-4 flex flex-col gap-2 md:mt-0 md:gap-0 md:px-4">
      {Array.from({ length: SKELETON_COUNT }, (_, i) => `skeleton-${i}`).map((key) => (
        <li
          key={key}
          className="flex gap-1 items-center py-2 md:gap-4 md:py-4 md:border-b md:border-gray-100 md:last:border-b-0"
        >
          <div className="w-9 h-9 shrink-0 rounded-2 skeleton-shimmer md:w-14 md:h-14 md:rounded-8" />
          <div className="flex flex-col gap-1 w-full overflow-hidden md:gap-2">
            <div className="h-4 w-16 rounded-2 skeleton-shimmer md:w-2/5" />
            <div className="h-3.5 w-4/5 rounded-2 skeleton-shimmer md:w-1/4" />
          </div>
        </li>
      ))}
    </ol>
  );
}

function RecentDailyLogsCardError() {
  return <p className="text-center text-body3 text-gray-500 py-10">Failed to load recent daily logs.</p>;
}

function RecentDailyLogsCardContent() {
  const recentDailyLogs = useRecentDailyLogsQuery();

  if (recentDailyLogs.length === 0) {
    return <div className="text-center text-body3 py-10">You haven't created any daily logs yet.</div>;
  }

  return (
    <ol className="mt-2 flex flex-col gap-2 md:mt-0 md:gap-0 md:divide-y md:divide-gray-100">
      {recentDailyLogs.map(({ document, projectName, projectId }, index) => (
        <li key={document.id}>
          <AppLink
            to="/projects/$projectId/log/$logId"
            params={{ projectId: String(projectId), logId: String(document.id) }}
            className="flex gap-1 items-center py-2 md:gap-4 md:items-start md:px-4 md:py-4 md:hover:bg-gray-10"
            onClick={() =>
              EventLogger.click(CLIENT_EVENT.HOME_RECENT_LOG_CLICK, {
                project_id: String(projectId),
                log_id: String(document.id),
                position: index,
              })
            }
          >
            <img src="/assets/images/icons/doc.svg" alt="" className="w-9 h-9 shrink-0 md:hidden" />
            <DateTile date={getWorkDate(document)} />
            <div className="flex overflow-hidden flex-col flex-1 gap-1 min-w-0 md:gap-1.5 md:justify-center md:self-stretch">
              <span className="text-gray-900 w-fit md:hidden">
                <Chip size="xs">{projectName}</Chip>
              </span>
              <div className="flex gap-2 items-center min-w-0">
                <p className="truncate text-body4 md:text-subtitle4 md:text-gray-1000">{document.title}</p>
                {document.locked && document.modifier && (
                  <span className="hidden gap-1 items-center px-2 py-0.5 whitespace-nowrap rounded-circle shrink-0 bg-red-10 text-label3 text-red-600 md:inline-flex">
                    <Lock width={12} height={12} aria-hidden="true" />
                    {`Being edited by ${document.modifier.name}`}
                  </span>
                )}
              </div>
              <p className="hidden gap-2 items-center text-label3 text-gray-600 md:flex">
                <span className="px-2 py-0.5 text-gray-800 bg-gray-100 rounded-2">{projectName}</span>
                {`Updated ${dayjs(document.modified_at).format("ddd, MMM D, YYYY HH:mm")}`}
              </p>
            </div>
          </AppLink>
        </li>
      ))}
    </ol>
  );
}

export function RecentDailyLogsCard() {
  return (
    <div className="p-4 bg-white rounded-8 shadow-box md:p-0 md:border md:border-gray-200 md:overflow-hidden">
      <div className="md:px-4 md:py-3 md:border-b md:border-gray-200">
        <h2 className="text-subtitle4 text-gray-1000">Recent Daily Logs</h2>
      </div>
      <div>
        <ErrorBoundary fallback={<RecentDailyLogsCardError />}>
          <Suspense fallback={<RecentDailyLogsCardSkeleton />}>
            <RecentDailyLogsCardContent />
          </Suspense>
        </ErrorBoundary>
      </div>
    </div>
  );
}
